import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
    Button, Input, FormGroup, Label, Form, Row, Col, 
    Card, CardBody, InputGroup, InputGroupText, Badge 
} from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiUser, FiMail, FiPhone, FiLock, FiEye, FiEyeOff, FiArrowLeft, FiSave } from "react-icons/fi";

const EditStaff = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        id: id,
        name: "",
        email: "",
        phone: "",
        password: ""
    });

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});

    // 1. Fetch Existing Data
    useEffect(() => {
        const fetchStaffData = async () => {
            dispatch(loadingTrue());
            try {
                const res = await Handler({
                    method: "GET",
                    url: `/staff/view.php?id=${id}`
                });

                if (res?.success) {
                    const staff = res.data[0] || res.data;
                    setFormData({
                        id: staff.id,
                        name: staff.name,
                        email: staff.email,
                        phone: staff.phone,
                        password: "" // Blank initially
                    });
                } else {
                    toast.error("Staff record not found!");
                    navigate("/staffs");
                }
            } catch (err) {
                toast.error("Failed to load staff details");
            } finally {
                dispatch(loadingFalse());
            }
        };

        fetchStaffData();
    }, [id, dispatch, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    // 🔥 Validation Logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.name.trim()) {
            formErrors.name = "Staff name is required";
            isValid = false;
        }

        if (!formData.email.trim()) {
            formErrors.email = "Email is required";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            formErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!formData.phone.trim()) {
            formErrors.phone = "Phone number is required";
            isValid = false;
        } else if (formData.phone.length !== 10) {
            formErrors.phone = "Phone must be 10 digits";
            isValid = false;
        }

        // Password logic: Edit-la blank vitta update panna koodathu, aana fill panna min 6 chars irukanum
        if (formData.password && formData.password.length < 6) {
            formErrors.password = "New password must be at least 6 characters";
            isValid = false;
        }

        setErrors(formErrors);
        if (!isValid) toast.error("Check the highlighted errors!");
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "PUT",
                url: "/staff/update.php",
                data: formData
            });

            if (res?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Profile Updated!',
                    text: 'Staff information has been saved.',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate("/staffs");
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Update process failed");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server connection failed", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-3 bg-light min-vh-100">
            {/* Header Section */}
            <div className="d-md-flex justify-content-between align-items-center mb-4">
                <div className="mb-3 mb-md-0">
                    <h4 className="fw-bold m-0 text-dark">Update Staff Profile</h4>
                    <p className="text-muted small m-0">Editing record for ID: <Badge color="primary" pill>#{id}</Badge></p>
                </div>
                <Button 
                    color="white" 
                    className="shadow-sm border rounded-pill px-4 d-flex align-items-center gap-2 hover-bg-light" 
                    onClick={() => navigate(-1)}
                >
                    <FiArrowLeft /> Back to List
                </Button>
            </div>

            <Row className="justify-content-center">
                <Col lg={10}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="bg-primary p-1"></div>
                        <CardBody className="p-4 p-md-5">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">
                                    <Col md={12}>
                                        <h6 className="text-primary fw-bold text-uppercase ls-1 mb-3">Employee Credentials</h6>
                                    </Col>

                                    {/* Name */}
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted text-uppercase">Full Name</Label>
                                            <InputGroup className="input-group-merge border rounded-3 overflow-hidden shadow-none bg-light">
                                                <InputGroupText className="bg-transparent border-0"><FiUser className="text-muted" /></InputGroupText>
                                                <Input
                                                    type="text"
                                                    name="name"
                                                    placeholder="Enter staff name"
                                                    className={`bg-transparent border-0 py-2 ${errors.name ? "is-invalid" : ""}`}
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                />
                                            </InputGroup>
                                            {errors.name && <small className="text-danger mt-1 d-block">{errors.name}</small>}
                                        </FormGroup>
                                    </Col>

                                    {/* Email */}
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted text-uppercase">Email Address</Label>
                                            <InputGroup className="input-group-merge border rounded-3 overflow-hidden shadow-none bg-light">
                                                <InputGroupText className="bg-transparent border-0"><FiMail className="text-muted" /></InputGroupText>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    placeholder="staff@muthaiah.com"
                                                    className={`bg-transparent border-0 py-2 ${errors.email ? "is-invalid" : ""}`}
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                />
                                            </InputGroup>
                                            {errors.email && <small className="text-danger mt-1 d-block">{errors.email}</small>}
                                        </FormGroup>
                                    </Col>

                                    {/* Phone */}
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted text-uppercase">Phone Number</Label>
                                            <InputGroup className="input-group-merge border rounded-3 overflow-hidden shadow-none bg-light">
                                                <InputGroupText className="bg-transparent border-0"><FiPhone className="text-muted" /></InputGroupText>
                                                <Input
                                                    type="text"
                                                    name="phone"
                                                    maxLength="10"
                                                    placeholder="10 digit contact number"
                                                    className={`bg-transparent border-0 py-2 ${errors.phone ? "is-invalid" : ""}`}
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                />
                                            </InputGroup>
                                            {errors.phone && <small className="text-danger mt-1 d-block">{errors.phone}</small>}
                                        </FormGroup>
                                    </Col>

                                    {/* Password */}
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted text-uppercase">Reset Password</Label>
                                            <InputGroup className="input-group-merge border rounded-3 overflow-hidden shadow-none bg-light">
                                                <InputGroupText className="bg-transparent border-0"><FiLock className="text-muted" /></InputGroupText>
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Keep blank if not changing"
                                                    className={`bg-transparent border-0 py-2 ${errors.password ? "is-invalid" : ""}`}
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                />
                                                <InputGroupText 
                                                    className="bg-transparent border-0" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                                </InputGroupText>
                                            </InputGroup>
                                            {errors.password ? <small className="text-danger mt-1 d-block">{errors.password}</small> : <small className="text-muted x-small italic">Optional update</small>}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Buttons */}
                                <div className="mt-5 pt-4 border-top d-flex flex-column flex-md-row gap-3">
                                    <Button 
                                        type="button" 
                                        color="light" 
                                        className="fw-bold px-4 py-2 rounded-3 order-2 order-md-1"
                                        onClick={() => navigate("/staffs")}
                                    >
                                        Cancel & Exit
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        color="primary" 
                                        className="add-btn fw-bold px-5 py-2 rounded-3 shadow d-flex align-items-center justify-content-center gap-2 order-1 order-md-2 ms-md-auto"
                                    >
                                        <FiSave /> Save Changes
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style>
                {`
                    .input-group-merge .form-control:focus {
                        background-color: #fff !important;
                        box-shadow: none !important;
                    }
                    .ls-1 { letter-spacing: 0.5px; }
                    .hover-bg-light:hover { background-color: #f8f9fa !important; }
                `}
            </style>
        </div>
    );
};

export default EditStaff;